'use client'

import { Button } from '@/components/ui/button'
import { BodyText } from '@/components/ui/typography'
import { BackgroundGradient } from '@/components/ui/background-gradient'
import { Link } from '@/i18n/routing'
import { IconArrowRight } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Logo from '../logo'

export type CTAVariant =
  | 'primary'
  | 'dictionary'
  | 'stories'
  | 'flashcards'
  | 'reader'
  | 'lists'

interface CTACardProps {
  variant: CTAVariant
  title?: string
  description?: string
  className?: string
}

const ctaHrefs: Record<CTAVariant, string> = {
  primary: '/dict',
  dictionary: '/dict',
  stories: '/read',
  flashcards: '/collections',
  reader: '/read/import',
  lists: '/collections'
}

export function CTACard({
  variant,
  title,
  description,
  className
}: CTACardProps) {
  const t = useTranslations('cta')
  const configKey = variant as keyof typeof ctaHrefs

  const config = {
    title: title || t(`${configKey}.title`),
    description: description || t(`${configKey}.description`),
    href: ctaHrefs[configKey],
    buttonText: t(`${configKey}.button`)
  }

  return (
    <div
      className={cn(
        'group border-border/50 bg-card/80 hover:border-border relative w-full overflow-hidden rounded-3xl border p-8 shadow-xl backdrop-blur-sm transition-all hover:shadow-2xl sm:p-10',
        className
      )}
    >
      {/* Large transparent logo on the left background */}
      <div className="pointer-events-none absolute top-1/2 -left-8 -translate-y-1/2 opacity-[0.35] transition-opacity group-hover:opacity-[0.45] dark:opacity-[0.15] dark:group-hover:opacity-[0.2]">
        <Logo className="text-foreground h-[400px] w-[400px]" />
      </div>

      {/* Subtle gradient overlay */}
      <div className="from-card/80 to-card/50 pointer-events-none absolute inset-0 bg-linear-to-r via-transparent" />

      {/* Content */}
      <div className="relative z-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1 space-y-3 sm:max-w-2xl">
          <h3 className="text-2xl leading-tight font-bold tracking-tight sm:text-3xl">
            {title || config.title}
          </h3>
          <BodyText className="text-muted-foreground text-base leading-relaxed sm:text-lg">
            {description || config.description}
          </BodyText>
        </div>

        <div className="mx-auto shrink-0 sm:mr-0 sm:ml-8">
          <BackgroundGradient containerClassName="rounded-xl">
            <Button
              size="lg"
              asChild
              className="group relative h-12 px-8 text-base font-semibold shadow-lg transition-all hover:scale-105 hover:shadow-xl"
            >
              <Link href={config.href}>
                {config.buttonText}
                <IconArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </BackgroundGradient>
        </div>
      </div>
    </div>
  )
}
